import {PlayerId, useGameServerConnection, useScene} from '@glark-newco/game-library';
import {Box, Button, Group, Modal, Text} from '@mantine/core';
import {useCallback, useEffect, useState} from 'react';
import {
    useClearActiveEdit,
    useSelectedNodes,
    useShowConsentModal,
} from '../state/networkEditStore';
import {useAddLinkByNodeId} from '../state/networkHooks';
import {networkStore} from '../state/networkStore';
import {AssignLinkEvent, LinkRelationship} from '../SupplyChainPayloadTypes';

export function LinkConsentModal() {
    const opened = useShowConsentModal();
    const {publishPlayerAction} = useGameServerConnection();
    const selectedNodes = useSelectedNodes();
    const scene = useScene();
    const clearActiveEdit = useClearActiveEdit();
    const addLinkByNodeId = useAddLinkByNodeId();

    const [selectedType, setSelectedType] = useState<LinkRelationship | null>(null);

    const getPlayerIdByNodeId = (nodeId: number): PlayerId | undefined =>
        networkStore.getState().ownership[nodeId];
    const playerA = selectedNodes[0] !== undefined ? getPlayerIdByNodeId(selectedNodes[0]) : undefined;
    const playerB = selectedNodes[1] !== undefined ? getPlayerIdByNodeId(selectedNodes[1]) : undefined;

    // Reset selected type when modal opens
    useEffect(() => {
        if (opened) {
            setSelectedType(null);
        }
    }, [opened]);

    const handleConfirm = useCallback(() => {
        if (!selectedType || selectedNodes.length !== 2) return;

        const [nodeA, nodeB] = selectedNodes;

        if (scene !== 'PLAY') {
            addLinkByNodeId(nodeA, nodeB, selectedType);
        } else {
            const ownerA = getPlayerIdByNodeId(nodeA);
            const ownerB = getPlayerIdByNodeId(nodeB);
            
            if (ownerA && ownerB) {
                publishPlayerAction({
                    type: 'assignLink',
                    playerA: ownerA,
                    playerB: ownerB,
                    relationship: selectedType,
                } as AssignLinkEvent);
            }
        }

        clearActiveEdit();
    }, [selectedType, selectedNodes, scene, addLinkByNodeId, publishPlayerAction, clearActiveEdit]);

    const handleCancel = useCallback(() => {
        clearActiveEdit();
    }, [clearActiveEdit]);

    return (
        <Modal opened={opened} onClose={handleCancel} centered title="Create Rope">
            <Box p="md">
                {/* Display selected players/nodes */}
                <Text mb="md" style={{textAlign: 'center'}}>
                    {playerA && playerB ? (
                        <>Between <strong>{playerA}</strong> and <strong>{playerB}</strong></>
                    ) : (
                        <>Between selected nodes</>
                    )}
                </Text>

                {/* Type selection */}
                <Text mb="sm" style={{textAlign: 'center'}}>Select rope type:</Text>
                <Box style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px'}}>
                    <Button data-testid="CreateLocalLink" onClick={() => setSelectedType('local')} color="red"
                        variant={selectedType === 'local' ? 'light' : 'outline'} style={{height: 'initial'}}>
                        <Group justify="center">
                            <img style={{width: '100%'}} src="images/local.svg" alt="Local"/>
                            <Text>Local</Text>
                        </Group>
                    </Button>
                    <Button data-testid="CreateRemoteLink" onClick={() => setSelectedType('remote')} color="green"
                        variant={selectedType === 'remote' ? 'light' : 'outline'} style={{height: 'initial'}}>
                        <Group justify="center">
                            <img style={{width: '100%'}} src="images/remote.svg" alt="Remote"/>
                            <Text>Remote</Text>
                        </Group>
                    </Button>
                </Box>

                {/* Confirm/Cancel buttons */}
                <Group justify="space-between" mt="md">
                    <Button variant="subtle" onClick={handleCancel} data-testid="CancelLinkButton">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={!selectedType} data-testid="ConfirmLinkButton">
                        Confirm
                    </Button>
                </Group>
            </Box>
        </Modal>
    );
}
